'use client';

import React, { useState, useEffect, useCallback } from 'react';
import { NotificationPayload } from '../types';

// ===== TYPES =====
export interface NotificationDropdownProps {
  apiBaseUrl?: string;
  websocketUrl?: string;
  limit?: number;
  showUnreadOnly?: boolean;
  onNotificationClick?: (notification: NotificationPayload) => void;
  onMarkAsRead?: (id: string) => Promise<boolean>;
  onDelete?: (id: string) => Promise<boolean>;
  onMarkAllAsRead?: () => Promise<void>;
  className?: string;
  buttonClassName?: string;
  contentClassName?: string;
  translations?: {
    title?: string;
    loading?: string;
    noNotifications?: string;
    allCaughtUp?: string;
    markAllRead?: string;
    markAsRead?: string;
    delete?: string;
    viewMore?: string;
    viewAllNotifications?: string;
    prev?: string;
    next?: string;
    page?: string;
  };
  // UI Component props for custom styling
  Button?: React.ComponentType<any>;
  Badge?: React.ComponentType<any>;
  DropdownMenu?: React.ComponentType<any>;
  DropdownMenuContent?: React.ComponentType<any>;
  DropdownMenuItem?: React.ComponentType<any>;
  DropdownMenuLabel?: React.ComponentType<any>;
  DropdownMenuSeparator?: React.ComponentType<any>;
  DropdownMenuTrigger?: React.ComponentType<any>;
  ScrollArea?: React.ComponentType<any>;
  Icons?: {
    bell?: React.ComponentType<any>;
    loader?: React.ComponentType<any>;
    check?: React.ComponentType<any>;
    x?: React.ComponentType<any>;
    chevronDown?: React.ComponentType<any>;
    chevronUp?: React.ComponentType<any>;
    externalLink?: React.ComponentType<any>;
    quote?: React.ComponentType<any>;
    alertCircle?: React.ComponentType<any>;
  };
  cn?: (classes: string) => string;
  formatDistanceToNow?: (date: Date, options?: any) => string;
  toast?: {
    success: (message: string) => void;
    error: (message: string) => void;
  };
}

// ===== UTILITIES =====
const priorityColors: Record<string, { icon: string; bg: string; dot: string; iconColor: string }> = {
  urgent: {
    icon: 'alertCircle',
    bg: 'bg-red-50',
    dot: 'bg-red-500',
    iconColor: 'text-red-500',
  },
  high: {
    icon: 'alertCircle',
    bg: 'bg-orange-50',
    dot: 'bg-orange-400',
    iconColor: 'text-orange-400',
  },
  normal: {
    icon: 'bell',
    bg: 'bg-blue-50',
    dot: 'bg-blue-500',
    iconColor: 'text-blue-500',
  },
  low: {
    icon: 'bell',
    bg: 'bg-gray-100',
    dot: 'bg-gray-400',
    iconColor: 'text-gray-400',
  },
};

// Utility to strip markdown and extra whitespace
function stripMarkdown(text: string): string {
  if (!text) return '';
  // Remove markdown bold/italic/code, headings, lists, blockquotes, etc.
  return text
    .replace(/\*\*([^*]+)\*\*/g, '$1') // bold
    .replace(/\*([^*]+)\*/g, '$1') // italic
    .replace(/`([^`]+)`/g, '$1') // inline code
    .replace(/^#+\s+/gm, '') // headings
    .replace(/^>\s+/gm, '') // blockquotes
    .replace(/^-\s+/gm, '') // lists
    .replace(/\n{2,}/g, '\n') // extra newlines
    .replace(/\s{2,}/g, ' ') // extra spaces
    .trim();
}

// ===== MAIN COMPONENT =====
export function NotificationDropdown({
  apiBaseUrl = '/api/notifications',
  websocketUrl,
  limit = 10,
  showUnreadOnly = false,
  onNotificationClick,
  onMarkAsRead,
  onDelete,
  onMarkAllAsRead,
  className = '',
  buttonClassName = '',
  contentClassName = '',
  translations = {},
  // UI Components
  Button,
  Badge,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  ScrollArea,
  Icons,
  cn,
  formatDistanceToNow,
  toast
}: NotificationDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [hoveredId, setHoveredId] = useState<number | null>(null);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [notifications, setNotifications] = useState<NotificationPayload[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [stats, setStats] = useState<{ total: number; unread: number; read: number } | null>(null);
  const [ws, setWs] = useState<WebSocket | null>(null);
  const [wsErrorLogged, setWsErrorLogged] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  // Default translations
  const t = {
    title: 'Notifications',
    loading: 'Loading...',
    noNotifications: 'No notifications',
    allCaughtUp: 'You\'re all caught up!',
    markAllRead: 'Mark all read',
    markAsRead: 'Mark as read',
    delete: 'Delete',
    viewMore: 'View more',
    viewAllNotifications: 'View all notifications',
    prev: 'Prev',
    next: 'Next',
    page: 'Page {page} of {pageCount}',
    ...translations
  };

  // ===== API FUNCTIONS =====
  const fetchNotifications = useCallback(async (pageNum = page) => {
    try {
      setLoading(true);
      setError(null);
      
      const params = new URLSearchParams();
      if (limit) params.append('limit', limit.toString());
      if (pageNum) params.append('page', pageNum.toString());
      if (showUnreadOnly) params.append('read', 'false');
      
      const response = await fetch(`${apiBaseUrl}?${params.toString()}`);
      const result = await response.json();
      
      console.log('[NotificationsDropdown] API Response:', result);
      
      if (result.success && Array.isArray(result.data)) {
        // Validate and clean notification data
        const validNotifications = result.data.map((notification: any) => ({
          id: notification.id || Date.now().toString(),
          recipientId: notification.recipientId || 'unknown',
          type: notification.type || 'info',
          title: notification.title || 'Notification',
          body: notification.body || notification.message || '',
          data: notification.data || {},
          meta: notification.meta || {},
          read: Boolean(notification.read),
          createdAt: notification.createdAt || notification.created_at || new Date().toISOString(),
          updatedAt: notification.updatedAt || notification.updated_at || new Date().toISOString(),
        }));
        
        // If showUnreadOnly is true, filter out read notifications on the client side too
        const filteredNotifications = showUnreadOnly 
          ? validNotifications.filter((n: any) => !n.read)
          : validNotifications;
        
        setNotifications(filteredNotifications);
        setTotal(filteredNotifications.length);
        setTotalPages(Math.ceil(filteredNotifications.length / limit));
      } else {
        console.warn('[NotificationsDropdown] No data or invalid data:', result.data);
        setNotifications([]);
        setTotal(0);
        setTotalPages(1);
        setError('No notifications found');
      }
    } catch (err) {
      setError('Failed to fetch notifications');
      console.error('Error fetching notifications:', err);
      setNotifications([]);
      setTotal(0);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  }, [apiBaseUrl, limit, showUnreadOnly, page]);

  const fetchStats = useCallback(async () => {
    try {
      const response = await fetch(`${apiBaseUrl}/stats`);
      const result = await response.json();
      
      if (result.success) {
        setStats(result.stats);
      }
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  }, [apiBaseUrl]);

  const markAsRead = useCallback(async (id: string | number) => {
    try {
      const idStr = id.toString();
      
      // Optimistically update the UI first
      setNotifications(prev => 
        prev.map(n => n.id === idStr ? { ...n, read: true } : n)
      );
      
      if (onMarkAsRead) {
        const success = await onMarkAsRead(idStr);
        if (!success) {
          // Revert on failure
          setNotifications(prev => 
            prev.map(n => n.id === idStr ? { ...n, read: false } : n)
          );
        } else if (toast) {
          toast.success(t.markAsRead);
        }
        return success;
      }
      
      const response = await fetch(`${apiBaseUrl}/${idStr}/read`, { method: 'PATCH' });
      const result = await response.json();
      
      if (!result.success) {
        // Revert on failure
        setNotifications(prev => 
          prev.map(n => n.id === idStr ? { ...n, read: false } : n)
        );
      } else if (toast) {
        toast.success(t.markAsRead);
      }
      
      return result.success;
    } catch (err) {
      console.error('Error marking as read:', err);
      // Revert on error
      setNotifications(prev => 
        prev.map(n => n.id === id.toString() ? { ...n, read: false } : n)
      );
      return false;
    }
  }, [apiBaseUrl, onMarkAsRead, toast, t.markAsRead]);

  const deleteNotification = useCallback(async (id: string | number) => {
    try {
      const idStr = id.toString();
      
      // Optimistically remove the notification from UI
      setNotifications(prev => prev.filter(n => n.id !== idStr));
      
      if (onDelete) {
        const success = await onDelete(idStr);
        if (!success) {
          // Revert on failure - we need to refetch to get the correct state
          fetchNotifications();
        } else if (toast) {
          toast.success(t.delete);
        }
        return success;
      }
      
      const response = await fetch(`${apiBaseUrl}/${idStr}`, { method: 'DELETE' });
      const result = await response.json();
      
      if (!result.success) {
        // Revert on failure - we need to refetch to get the correct state
        fetchNotifications();
      } else if (toast) {
        toast.success(t.delete);
      }
      
      return result.success;
    } catch (err) {
      console.error('Error deleting notification:', err);
      // Revert on error - we need to refetch to get the correct state
      fetchNotifications();
      return false;
    }
  }, [apiBaseUrl, onDelete, toast, t.delete, fetchNotifications]);

  const markAllAsRead = useCallback(async () => {
    try {
      // Optimistically update all notifications as read
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      
      if (onMarkAllAsRead) {
        try {
          await onMarkAllAsRead();
          if (toast) toast.success(t.markAllRead);
        } catch (err) {
          // Revert on failure
          setNotifications(prev => prev.map(n => ({ ...n, read: false })));
          throw err;
        }
        return;
      }

      // Call the mark-all-read API endpoint
      try {
        const response = await fetch(`${apiBaseUrl}/mark-all-read`, { method: 'PATCH' });
        const result = await response.json();
        
        if (!result.success) {
          // Revert on failure
          setNotifications(prev => prev.map(n => ({ ...n, read: false })));
        } else if (toast) {
          toast.success(t.markAllRead);
        }
      } catch (err) {
        // Revert on error
        setNotifications(prev => prev.map(n => ({ ...n, read: false })));
        console.error('Error marking all as read:', err);
      }
      
      // Mark all as read by updating each notification
      const unreadNotifications = notifications.filter(n => !n.read);
      for (const notification of unreadNotifications) {
        await markAsRead(notification.id);
      }
      if (toast) toast.success(t.markAllRead);
    } catch (err) {
      console.error('Error marking all as read:', err);
      // Revert on error
      setNotifications(prev => prev.map(n => ({ ...n, read: false })));
    }
  }, [notifications, markAsRead, onMarkAllAsRead, toast, t.markAllRead]);

  const goToPage = useCallback((pageNum: number) => {
    setPage(pageNum);
    fetchNotifications(pageNum);
  }, [fetchNotifications]);

  const refetch = useCallback(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  // ===== WEBSOCKET =====
  useEffect(() => {
    if (!websocketUrl) return;

    const connectWebSocket = () => {
      const websocket = new WebSocket(websocketUrl);
      
      websocket.onopen = () => {
        console.log('WebSocket connected');
        setWs(websocket);
      };
      
      websocket.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.type === 'notification' && data.event === 'notification:created') {
            // Add new notification to the list
            setNotifications(prev => [data.data, ...prev]);
            setStats(prev => prev ? { ...prev, total: prev.total + 1, unread: prev.unread + 1 } : null);
          }
        } catch (err) {
          console.error('Error parsing WebSocket message:', err);
        }
      };
      
      websocket.onclose = () => {
        console.log('WebSocket disconnected');
        setWs(null);
        // Reconnect after 3 seconds
        setTimeout(connectWebSocket, 3000);
      };
      
      websocket.onerror = (err) => {
        // Only log error once to avoid spam
        if (!wsErrorLogged) {
          console.warn('WebSocket connection failed - notifications will work without real-time updates');
          setWsErrorLogged(true);
        }
      };
    };

    connectWebSocket();

    return () => {
      if (ws) {
        ws.close();
      }
    };
  }, [websocketUrl]);

  // ===== COMBINED FETCH =====
  const fetchAll = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Fetch both notifications and stats in parallel
      const [notificationsResponse, statsResponse] = await Promise.all([
        fetch(`${apiBaseUrl}?${new URLSearchParams({
          limit: limit.toString(),
          page: page.toString(),
          ...(showUnreadOnly && { read: 'false' })
        })}`),
        fetch(`${apiBaseUrl}/stats`)
      ]);
      
      const [notificationsResult, statsResult] = await Promise.all([
        notificationsResponse.json(),
        statsResponse.json()
      ]);
      
      // Process notifications
      if (notificationsResult.success && Array.isArray(notificationsResult.data)) {
        const validNotifications = notificationsResult.data.map((notification: any) => ({
          id: notification.id || Date.now().toString(),
          recipientId: notification.recipientId || 'unknown',
          type: notification.type || 'info',
          title: notification.title || 'Notification',
          body: notification.body || notification.message || '',
          data: notification.data || {},
          meta: notification.meta || {},
          read: Boolean(notification.read),
          createdAt: notification.createdAt || notification.created_at || new Date().toISOString(),
          updatedAt: notification.updatedAt || notification.updated_at || new Date().toISOString(),
        }));
        
        const filteredNotifications = showUnreadOnly 
          ? validNotifications.filter((n: any) => !n.read)
          : validNotifications;
        
        setNotifications(filteredNotifications);
        setTotal(filteredNotifications.length);
        setTotalPages(Math.ceil(filteredNotifications.length / limit));
      } else {
        setNotifications([]);
        setTotal(0);
        setTotalPages(1);
        setError('No notifications found');
      }
      
      // Process stats
      if (statsResult.success) {
        setStats(statsResult.stats);
      }
    } catch (err) {
      setError('Failed to fetch data');
      console.error('Error fetching data:', err);
      setNotifications([]);
    } finally {
      setLoading(false);
    }
  }, [apiBaseUrl, limit, page, showUnreadOnly]);

  // ===== INITIAL LOAD =====
  useEffect(() => {
    // Add a small delay to prevent excessive calls during page transitions
    const timer = setTimeout(() => {
      fetchAll();
    }, 100);
    
    return () => clearTimeout(timer);
  }, []); // Only run once on mount

  // ===== HANDLERS =====
  const handleNotificationClick = async (notification: any) => {
    await markAsRead(notification.id);
    if (notification.link) {
      window.location.href = notification.link;
    }
    if (onNotificationClick) {
      onNotificationClick(notification);
    }
  };

  const getPriorityAvatar = (priority: string) => {
    const color = priorityColors[priority] || priorityColors['low'];
    const Icon = Icons?.[color.icon as keyof typeof Icons];
    return (
      <span className={`flex items-center justify-center rounded-full h-8 w-8 ${color.bg}`}>
        {Icon ? <Icon className={`h-4 w-4 ${color.iconColor}`} /> : <div className={`h-4 w-4 rounded ${color.iconColor.replace('text-', 'bg-')}`} />}
      </span>
    );
  };

  const getPriorityDotClass = (priority: string) => {
    const color = priorityColors[priority] || priorityColors['low'];
    return color.dot;
  };

  // Helper to ensure data is always an object with fallback values
  const getTranslationData = (data: any) => {
    if (!data || typeof data !== 'object' || Array.isArray(data)) {
      console.log('[NotificationsDropdown] No data or invalid data:', data);
      return {};
    }
    
    // Add fallback values for common variables to prevent translation errors
    const translationData = {
      updatedBy: data.updatedBy || 'System',
      changes: data.changes || '',
      plan: data.plan || '',
      activatedBy: data.activatedBy || 'System',
      deactivatedBy: data.deactivatedBy || 'System',
      reason: data.reason || '',
      expiredDate: data.expiredDate || '',
      ...data
    };
    
    return translationData;
  };

  // Helper to handle both translation keys and actual text
  const getNotificationText = (text: string, data: any) => {
    // Check if the text looks like a translation key (contains dots, is short, and no spaces)
    const isTranslationKey = text && text.includes('.') && text.length < 50 && !text.includes(' ');
    
    if (isTranslationKey) {
      try {
        // This would need to be passed as a prop for translation function
        return text; // For now, return as is
      } catch (error) {
        console.log('[NotificationsDropdown] Translation failed for key:', text, 'Error:', error);
        return text;
      }
    } else {
      return text;
    }
  };

  // Calculate unread count from actual notifications
  const unreadCount = notifications.filter(n => !n.read).length;
  const hasNext = page < totalPages;
  const hasPrev = page > 1;

  // Use provided UI components or fallback to basic HTML
  const ButtonComponent = Button || ((props: any) => <button {...props} />);
  const BadgeComponent = Badge || ((props: any) => <span {...props} />);
  const DropdownMenuComponent = DropdownMenu || ((props: any) => <div {...props} />);
  const DropdownMenuContentComponent = DropdownMenuContent || ((props: any) => <div {...props} />);
  const DropdownMenuItemComponent = DropdownMenuItem || ((props: any) => <div {...props} />);
  const DropdownMenuLabelComponent = DropdownMenuLabel || ((props: any) => <div {...props} />);
  const DropdownMenuSeparatorComponent = DropdownMenuSeparator || ((props: any) => <div {...props} />);
  const DropdownMenuTriggerComponent = DropdownMenuTrigger || ((props: any) => <button {...props} />);
  const ScrollAreaComponent = ScrollArea || ((props: any) => <div {...props} />);

  return (
    <DropdownMenuComponent open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTriggerComponent asChild>
        <ButtonComponent
          variant="outline"
          size="icon"
          className="h-9 w-9 relative"
        >
          {Icons?.bell ? <Icons.bell className="h-5 w-5" /> : <div className="h-5 w-5" />}
          {unreadCount > 0 && (
            <BadgeComponent
              className="h-5 min-w-5 rounded-full px-1 inline-flex justify-center items-center text-xs tabular-nums absolute -top-3 -right-3 z-50 bg-red-500 text-white"
              variant="destructive"
            >
              {unreadCount > 99 ? '99+' : unreadCount}
            </BadgeComponent>
          )}
          <span className="sr-only">Toggle notifications</span>
        </ButtonComponent>
      </DropdownMenuTriggerComponent>
      <DropdownMenuContentComponent align="start" className="w-80 p-0 shadow-xl rounded-xl">
        <div className="p-3 border-b flex items-center justify-between">
          <DropdownMenuLabelComponent className="text-base font-semibold pl-0">{t.title}</DropdownMenuLabelComponent>
          {unreadCount > 0 && (
            <ButtonComponent
              variant="ghost"
              size="sm"
              onClick={markAllAsRead}
              className="h-6 px-2 text-xs"
            >
              {t.markAllRead}
            </ButtonComponent>
          )}
        </div>
        <ScrollAreaComponent className="h-80">
          {loading ? (
            <div className="flex items-center justify-center p-6">
              {Icons?.loader ? <Icons.loader className="h-4 w-4 animate-spin" /> : <div className="h-4 w-4 animate-spin bg-gray-300 rounded" />}
              <span className="ml-2 text-sm text-muted-foreground">{t.loading}</span>
            </div>
          ) : notifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center p-8 text-center">
              {Icons?.bell ? <Icons.bell className="h-7 w-7 text-muted-foreground mb-2" /> : <div className="h-7 w-7 bg-gray-300 rounded mb-2" />}
              <p className="text-sm text-muted-foreground">{t.noNotifications}</p>
              <p className="text-xs text-muted-foreground">{t.allCaughtUp}</p>
            </div>
          ) : (
            <div>
                  {notifications.filter(notification => notification && notification.id).map((notification: any) => {
                    const dotClass = getPriorityDotClass(notification.meta?.priority || 'normal');
                    const isExpanded = expandedId === notification.id;
                    const isRead = notification.read;
                    return (
                      <div
                        key={notification.id}
                        className={`relative flex flex-col border-l-4 ${
                          isRead
                            ? 'bg-white border-l-transparent hover:bg-gray-50'
                            : 'bg-blue-50/30 border-l-blue-500 hover:bg-blue-50/50'
                        } ${notification.data?.link ? 'cursor-pointer' : ''}`}
                        style={{ minHeight: 64 }}
                        onMouseEnter={() => setHoveredId(notification.id)}
                        onMouseLeave={() => setHoveredId(null)}
                      >
                    {/* Main notification content */}
                    <div 
                      className="flex items-start gap-3 px-3 py-2 cursor-pointer"
                      onClick={() => setExpandedId(isExpanded ? null : notification.id)}
                    >
                        {/* Avatar with icon */}
                        <div className="flex-shrink-0 mt-0.5">
                          {getPriorityAvatar(notification.meta?.priority || 'normal')}
                        </div>
                        {/* Main content */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1">
                            <span className={`font-semibold text-xs truncate max-w-[120px] ${
                              isRead ? "text-muted-foreground" : "text-foreground"
                            }`} title={getNotificationText(notification.title, getTranslationData(notification.data))}>
                              {getNotificationText(notification.title, getTranslationData(notification.data))}
                            </span>
                            {!isRead && (
                              <span className={`ml-1 h-1.5 w-1.5 rounded-full inline-block ${dotClass}`} />
                            )}
                          </div>
                          <div className="text-xs text-muted-foreground mt-0.5 text-left line-clamp-1">
                            {stripMarkdown(getNotificationText(notification.body || notification.message, getTranslationData(notification.data)))}
                          </div>
                        {/* Time moved below */}
                            <div className="flex items-center justify-between mt-1">
                              <span className="text-[10px] text-muted-foreground">
                                {(() => {
                                  try {
                                    const dateStr = notification.created_at || notification.createdAt;
                                    if (!dateStr) return 'recently';
                                    const date = new Date(dateStr);
                                    if (isNaN(date.getTime())) return 'recently';
                                    return formatDistanceToNow ? formatDistanceToNow(date, { addSuffix: true }) : 'recently';
                                  } catch {
                                    return 'recently';
                                  }
                                })()}
                              </span>
                          <div className="flex items-center gap-1">
                            {/* Only show check if unread */}
                            {!isRead && (
                              <ButtonComponent
                                variant="ghost"
                                size="icon"
                                onClick={(e: any) => { e.stopPropagation(); markAsRead(notification.id); }}
                                className="h-5 w-5 p-0 opacity-70 hover:opacity-100"
                                title={t.markAsRead}
                              >
                                {Icons?.check ? <Icons.check className="h-3 w-3 text-green-600" /> : <div className="h-3 w-3 bg-green-600 rounded" />}
                              </ButtonComponent>
                            )}
                            {/* Show delete only on hover */}
                            {hoveredId === notification.id && (
                              <ButtonComponent
                                variant="ghost"
                                size="icon"
                                onClick={(e: any) => { e.stopPropagation(); deleteNotification(notification.id); }}
                                className="h-5 w-5 p-0 text-muted-foreground hover:text-destructive"
                                title={t.delete}
                              >
                                {Icons?.x ? <Icons.x className="h-3 w-3" /> : <div className="h-3 w-3 bg-gray-400 rounded" />}
                              </ButtonComponent>
                            )}
                            {/* Expand/collapse icon - removed onClick since parent handles it */}
                            <div className="h-5 w-5 flex items-center justify-center opacity-70">
                              {isExpanded ? 
                                (Icons?.chevronUp ? <Icons.chevronUp className="h-3 w-3" /> : <div className="h-3 w-3 bg-gray-400 rounded" />) : 
                                (Icons?.chevronDown ? <Icons.chevronDown className="h-3 w-3" /> : <div className="h-3 w-3 bg-gray-400 rounded" />)
                              }
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                        {/* Expanded content - full width */}
                        {isExpanded && (() => {
                          const dataObj = getTranslationData(notification.data);
                          return (
                            <div className="px-3 pb-2">
                              <div className="p-3 rounded-lg bg-gray-50/80 border border-gray-200 text-xs flex flex-col gap-2">
                                <div className="font-semibold text-sm break-words">{getNotificationText(notification.title, dataObj)}</div>
                                <div className="break-words whitespace-pre-line">{stripMarkdown(getNotificationText(notification.body || notification.message, dataObj))}</div>
                                {dataObj.adminNotes && dataObj.adminNotes.trim() !== '' && (
                                  <div className="mt-2 relative">
                                    <div className="flex items-start gap-2 p-3 bg-blue-50 border-l-4 border-blue-400 rounded-r-md">
                                      {Icons?.quote ? <Icons.quote className="h-4 w-4 text-blue-500 flex-shrink-0 mt-0.5" /> : <div className="h-4 w-4 bg-blue-500 rounded flex-shrink-0 mt-0.5" />}
                                      <div className="flex-1">
                                        <div className="text-xs font-semibold text-blue-800 mb-1">Admin notes:</div>
                                        <div className="text-xs text-gray-700">{dataObj.adminNotes}</div>
                                      </div>
                                      {Icons?.quote ? <Icons.quote className="h-4 w-4 text-blue-500 flex-shrink-0 mt-0.5 rotate-180" /> : <div className="h-4 w-4 bg-blue-500 rounded flex-shrink-0 mt-0.5 rotate-180" />}
                                    </div>
                                  </div>
                                )}
                                {notification.data?.link && (
                                  <a
                                    href={notification.data.link}
                                    className="mt-1 inline-flex items-center gap-1 text-blue-600 hover:underline text-xs font-medium"
                                    onClick={async (e: any) => {
                                      e.stopPropagation();
                                      await markAsRead(notification.id);
                                    }}
                                  >
                                    {Icons?.externalLink ? <Icons.externalLink className="h-3 w-3" /> : <div className="h-3 w-3 bg-blue-600 rounded" />} {t.viewMore}
                                  </a>
                                )}
                              </div>
                            </div>
                          );
                        })()}
                  </div>
                );
              })}
            </div>
          )}
        </ScrollAreaComponent>
        {/* Pagination controls */}
        <div className="flex items-center justify-between px-3 py-2 border-t bg-white">
          <ButtonComponent
            variant="ghost"
            size="sm"
            disabled={!hasPrev}
            onClick={() => goToPage(page - 1)}
          >
            {t.prev}
          </ButtonComponent>
          <span className="text-xs text-muted-foreground">
            {t.page.replace('{page}', page.toString()).replace('{pageCount}', totalPages.toString())}
          </span>
          <ButtonComponent
            variant="ghost"
            size="sm"
            disabled={!hasNext}
            onClick={() => goToPage(page + 1)}
          >
            {t.next}
          </ButtonComponent>
        </div>
        <DropdownMenuSeparatorComponent />
        <DropdownMenuItemComponent
          className="justify-center text-xs text-muted-foreground cursor-pointer py-2"
          onClick={() => {
            setIsOpen(false);
            window.location.href = '/dashboard/notifications';
          }}
        >
          {t.viewAllNotifications}
        </DropdownMenuItemComponent>
      </DropdownMenuContentComponent>
    </DropdownMenuComponent>
  );
}

export default NotificationDropdown;
